<template>
  <div class="d-flex flex-grow-1">
    <v-navigation-drawer
      :class="{ offline: isOffline }"
      v-model="drawer"
      app
      width="270"
      floating
      :right="$vuetify.rtl"
      :light="menuTheme === 'light'"
      :dark="menuTheme === 'dark'"
    >
      <template #prepend>
        <div class="pa-2">
          <div class="title font-weight-bold primary--text">
            {{ product.name }}
          </div>
          <div class="overline grey--text">
            {{ product.version }}
            <v-badge
              v-if="isDemoHotel"
              color="error"
              content="DEMO"
              inline
            />
          </div>
        </div>
      </template>

      <div class="arch-menu">
        <v-list-item v-if="showHotelSidebarLogo">
          <v-img :src="hotelSidebarLogo" />
        </v-list-item>
        <!-- props component, visible, loading below are for custom dialog menu -->
        <ArchNavMenu
          :key="i"
          :item="item"
          :component.sync="component"
          :visible.sync="visible"
          :loading.sync="loading"
          v-for="(item, i) in navigation.menu"
        />
      </div>
      <template #append>
        <div class="pa-1 text-center">
          <v-btn
            v-for="(item, index) in navigation.footer"
            :key="index"
            :href="item.href"
            :target="item.target"
            small
            text
          >
            {{ item.key ? $t(item.key) : item.text }}
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>
    <v-app-bar
      :class="{ offline: isOffline }"
      app
      :color="isToolbarDetached ? 'surface' : undefined"
      :flat="isToolbarDetached"
      :light="toolbarTheme === 'light'"
      :dark="toolbarTheme === 'dark'"
    >
      <v-card
        class="flex-grow-1 d-flex"
        :class="[isToolbarDetached ? 'pa-1 mt-3 mx-1' : 'pa-0 ma-0']"
        :flat="!isToolbarDetached"
      >
        <div class="d-flex flex-grow-1 align-center">
          <v-text-field
            v-if="showSearch"
            append-icon="mdi-close"
            placeholder="Search"
            prepend-inner-icon="mdi-magnify"
            hide-details
            solo
            flat
            autofocus
            @click:append="showSearch = false"
          />
          <div
            v-else
            class="d-flex flex-grow-1 align-center"
          >
            <v-app-bar-nav-icon
              @click.stop="drawer = !drawer"
            />
            <v-spacer class="d-none d-lg-block" />
            <v-menu
              v-model="searchResultVisible"
              :close-on-content-click="true"
              transition="slide-y-transition"
              offset-y
            >
              <template #activator="{ }">
                <v-text-field
                  ref="search"
                  v-model="searchKeyword"
                  class="mx-1 hidden-xs-only"
                  :placeholder="$t('data.searchBar')"
                  :name="`random-${Math.random()}`"
                  prepend-inner-icon="mdi-magnify"
                  hide-details
                  filled
                  rounded
                  clearable
                  @click="
                    () => {
                      if (
                        searchResultItems.length > 0 &&
                        searchKeyword.length > 0
                      ) {
                        searchResultVisible = true;
                      }
                    }
                  "
                  @keyup="showSearchResult"
                  @keydown.up.prevent="pressUp"
                  @keydown.down.prevent="pressDown"
                  @keydown.enter.prevent="pressEnter"
                  dense
                />
              </template>
              <v-card style="padding: 10px">
                <div
                  style="max-height: 400px; overflow: auto"
                  id="sr-container"
                >
                  <div
                    v-for="(item, i) in getSearchResultItems()"
                    :key="i"
                    :id="`sr-${i}`"
                  >
                    <div
                      style="padding: 5px; font-size: smaller; color: #b9b2b2"
                      v-html="item.parentText"
                    />
                    <div style="padding-left: 20px">
                      <v-list-item
                        link
                        dense
                        v-for="(child, j) in item.items"
                        :key="`search-${j}`"
                        :to="child.isCustomMenu ? undefined : $parseURL(child.link)"
                        :input-value="selected === i"
                        :style="selected === i && `background: #CFD8DC;`"
                        @click="() => onClick(child)"
                      >
                        <v-list-item-icon>
                          <v-icon
                            v-text="
                              $i(child.key.slice(child.key.indexOf('.') + 1))
                            "
                          />
                        </v-list-item-icon>
                        <v-list-item-title>
                          {{
                            child.text || $t(child.key)
                          }}
                        </v-list-item-title>
                      </v-list-item>
                    </div>
                  </div>
                </div>
              </v-card>
            </v-menu>
            <v-spacer class="d-block d-sm-none" />
            <v-btn
              class="d-block d-sm-none"
              icon
              @click="showSearch = true"
            >
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
            <ToolbarHotels />
            <toolbar-language />
            <!-- <div :class="[$vuetify.rtl ? 'ml-1' : 'mr-1']">
              <toolbar-notifications />
            </div> -->
            <toolbar-user />
          </div>
        </div>
      </v-card>
    </v-app-bar>
    <v-main>
      <st-announcement-component :announcement-app="announcementApp">
        <v-container
          class="fill-height"
          :fluid="!isContentBoxed"
        >
          <v-layout>
            <slot />
          </v-layout>
        </v-container>
      </st-announcement-component>
    </v-main>
    <component
      :is="component"
      :visible.sync="visible"
      :loading="loading"
      @close="close"
    />
  </div>
</template>

<script lang="ts">
import config from "../configs";
import MainMenu from "../components/navigation/MainMenu.vue";
import ToolbarUser from "../components/toolbar/ToolbarUser.vue";
import ToolbarLanguage from "../components/toolbar/ToolbarLanguage.vue";
import ToolbarHotels from "../components/toolbar/ToolbarHotels.vue";
import ToolbarNotifications from "../components/toolbar/ToolbarNotifications.vue";
import ArchNavMenu from "@/components/navigation/ArchNavMenu.vue";
import navigation from "@/configs/navigation";
import reports from "@/configs/reports";
import PermissionHelper from "@/permissions/PermissionHelper";
import { PermissionEnum } from "@/permissions/PermissionEnum";
// import others from "@/configs/others";
import { Component, Vue, Watch } from "vue-property-decorator";
import { AnnouncementApp } from "sentec-vues/modules/announcement/enums/AnnouncementApp";
import { GenericCrudRequestQueryBuilder } from "sentec-core-ts";
import ApiConfig from "@/services/Api-config";

@Component({
  components: {
    MainMenu,
    ToolbarUser,
    ToolbarLanguage,
    ToolbarNotifications,
    ToolbarHotels,
    ArchNavMenu,
    // list custom component will be loaded dynamically when needed
    "test-custom-menu": () => import("@/components/navigation/TestCustomMenu.vue"),
    "close-income-statement": () => import("@/components/common/CloseIncomeStatement.vue"),
    "average-cost-calculation-dialog": () => import("@/components/entry/averageCostCalculationEntry/Dialog.vue")
  },
})
export default class DefaultLayout extends Vue {
  drawer: any = null;
  showSearch: any = false;
  searchKeyword: any = "";
  searchResultVisible: any = false;
  searchResultItems: any[] = [];
  navigation: any = config.navigation;
  menus: any[] = [];
  selected = 0;
  $refs: any;
  get announcementApp() {
    return AnnouncementApp.FINANCE;
  }
  get state() {
    return this.$store.state.app;
  }
  get isOffline() {
    return this.state.isOffline;
  }
  get product() {
    return this.state.product;
  }
  get isContentBoxed() {
    return this.state.isContentBoxed;
  }
  get menuTheme() {
    return this.state.isOff;
  }
  get toolbarTheme() {
    return this.state.toolbarTheme;
  }
  get isToolbarDetached() {
    return this.state.isToolbarDetached;
  }
  get isDemoHotel(){
    return Number(this.$route.query.hotelId) <= 100;
  }
  onKeyup(e) {
    this.$refs.search.focus();
  }
  updateMenuKeyword(menu: any) {
    if (menu.items) {
      menu.items.forEach((item) => {
        this.updateMenuKeyword(item);
      });
    }
    let text = menu.text || this.$t(menu.key);
    let temp = text.toLowerCase().split(" ");
    if (temp.length > 1) {
      var newKeyword = "";
      temp.forEach((item) => {
        newKeyword += item.substring(0, 1);
      });
      if (!menu.keyword) {
        menu.keywords = newKeyword;
      } else {
        menu.keywords += " " + newKeyword;
      }
    }
  }
  mounted() {
    this.hotelId = this.$route.query.hotelId;
    if (this.menus.length == 0) {
      navigation.menu.forEach((i) => {
        this.updateMenuKeyword(i);
      });
    }
    this.menus = navigation.menu;
    this.loadCurrentHotel();
  }
  showSearchResult(ev) {
    if (this.searchKeyword && this.searchKeyword.length > 0) {
      let keyword = this.searchKeyword.toLowerCase();
      const items = [...this.menus, ...reports.reports];
      this.searchResultItems = [];
      this.initSearchResultItems({ items }, {}, keyword);
      this.searchResultVisible = true;
    } else {
      this.searchResultVisible = false;
    }
  }
  initSearchResultItems(item, parent, keyword) {
    let self = this;
    if (item.items != null) {
      item.items.forEach((i) => {
        let theItem = item;
        theItem.parentText = parent.text || this.$t(parent.key);
        self.initSearchResultItems(i, theItem, keyword);
      });
    } else {
      let textToSearch =
        (item.text || "") +
        (this.$t(item.key) || "") +
        (item.keywords || "") +
        (parent.text || "") +
        (this.$t(parent.key) || "") +
        (parent.keywords || "");
      let keywords = keyword.split(" ");
      let valid = true;
      let parentText = parent.text || this.$t(parent.key);
      for (let k of keywords) {
        if (textToSearch.toLowerCase().indexOf(k) == -1) {
          valid = false;
        }
      }
      if (valid && PermissionHelper.canAccess({ type: PermissionEnum.MENU, item: item })) {
        if (parent.parentText && parent.parentText.length > 0) {
          parentText =
            parent.parentText +
            " <span style='color: black !important;'>&rarr;</span> " +
            parentText;
        }
        this.searchResultItems.push({
          parentText,
          items: [item],
        });
      }
    }
  }

  getSearchResultItems() {
    let parents: any[] = [];
    let items: any[] = [];
    this.searchResultItems.forEach((i) => {
      let item: any = {
        ...i,
      };
      if (parents.indexOf(i.parentText) != -1) {
        item.parentText = null;
      } else {
        parents.push(i.parentText);
      }
      items.push(item);
    });
    return items;
  }
  pressUp() {
    if (this.selected > 0) {
      let temp = this.selected - 1;
      this.selected = temp;
      let cont = document.getElementById("sr-container");
      let item = document.getElementById(`sr-${temp}`);
      if (Number(item?.offsetTop) < Number(cont?.scrollTop)) {
        cont?.scrollTo({ top: Number(item?.offsetTop) });
      }
    }
  }
  pressDown() {
    let res = this.getSearchResultItems();
    if (this.selected < res.length -1) {
      let temp = this.selected + 1;
      this.selected = temp;
      let cont = document.getElementById("sr-container");
      let item = document.getElementById(`sr-${temp}`);
      if (Number(item?.offsetTop) + Number(item?.clientHeight) > (Number(cont?.scrollTop) +Number(cont?.clientHeight))) {
        cont?.scrollTo({ top: Number(item?.offsetTop) + Number(item?.clientHeight) - Number(cont?.clientHeight) });
      }
    }
  }
  pressEnter() {
    let res = this.getSearchResultItems();
    if (res[this.selected].items[0].isCustomMenu) {
      this.onClick(res[this.selected].items[0]);
    } else {
      this.$router.push(res[this.selected].items[0].link).catch(() => {});
    }
    setTimeout(() => {
      this.searchResultVisible = false;
    }, 500);
  }
  @Watch("searchResultVisible")
  onVisibleMenuChanged(n,p) {
    if (!n && p) {
      this.selected = 0;
      let cont = document.getElementById("sr-container");
      cont?.scrollTo({ top: 0 });
    }
  }
  @Watch("searchResultItems")
  onItemsChanged(n,p) {
    if (n.length !== p.length) {
      this.selected = 0;
      let cont = document.getElementById("sr-container");
      cont?.scrollTo({ top: 0 });
    }
  }
  // states & methods needed for custom dialog menu component
  component = "";
  visible = false;
  loading = false;
  close() {
      this.visible = false;
  }
  onClick(item) {
    if (item.isCustomMenu) {
      this.component = item.component;
      setTimeout(() => {
        this.visible = true;
      }, 200);
    }
  }
  hotelId: any = 0;
  showHotelSidebarLogo: boolean = false;
  hotelSidebarLogo: string = "";
  async loadCurrentHotel() {
    const query = new GenericCrudRequestQueryBuilder()
      .filter("id", this.hotelId)
      .build();
    const res = await this.$http.get(`/hotel/${this.hotelId}/dto/public.hotel/v1?${query}`);

    if (res.data?.Data?.length > 0) {
      const hotel = res.data.Data[0];
      this.showHotelSidebarLogo = hotel.show_sidebar_menu_logo;
      this.hotelSidebarLogo = hotel.sidebar_menu_logo_url ? ApiConfig.ST_STORAGE_STATIC_URL + String(hotel.sidebar_menu_logo_url) : "";
    }
  }
}
</script>
<style>
.offline {
  top: 25px !important;
}
.v-navigation-drawer .fa:before,
.v-navigation-drawer .fas:before {
  font-size: 18px !important;
}
.arch-menu .v-list-item {
  border-radius: 5px;
}
.arch-menu .v-list-item--active {
  color: var(--v-primary-base);
}
</style>
